import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event.
   * PAP: Re-renders the gallery from the fetched HTML so the variant image (slide 4)
   * is available, then navigates to it. Hero images (slides 1-3) remain in place.
   * The target slide index is read from data-pap-variant-slide on the new gallery element.
   * If no variant image exists for the selection (index = -1), the gallery is left unchanged.
   *
   * The Liquid uses selected_or_first_available_variant for option_values= requests (AJAX)
   * and selected_variant for variant= requests (direct URL), so pap-variant-slide is always
   * correctly set in the fetched HTML regardless of which param variant-picker.js uses.
   */
  #handleVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;

    const newGallery = html.querySelector('media-gallery');
    if (!newGallery) return;

    const variantSlideIdx = parseInt(newGallery.getAttribute('data-pap-variant-slide') ?? '-1', 10);

    // Sync the attribute on the live element so the page-load script stays consistent
    this.setAttribute('data-pap-variant-slide', String(variantSlideIdx));

    // Fade out → swap all slides → fade in. Prevents the jarring flash from a full
    // innerHTML replacement while hero slides briefly disappear mid-transition.
    this.classList.add('pap-updating');
    setTimeout(() => {
      this.innerHTML = newGallery.innerHTML;
      if (variantSlideIdx >= 0) {
        const slideshow = this.querySelector('slideshow-component') ?? this.refs?.slideshow;
        slideshow?.select(variantSlideIdx, undefined, { animate: false });
      }
      // Two rAFs ensure the browser has painted the new DOM before fading back in.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.remove('pap-updating');
        });
      });
    }, 120);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
